import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import './Link.css';

export interface LinkProps {
  to?: string;
  href?: string;
  children: React.ReactNode;
  variant?: 'default' | 'nav' | 'footer';
  className?: string;
  external?: boolean;
  onClick?: () => void;
  /** When true, link is active when pathname starts with `to` (e.g. /blog/post/1 for to="/blog"). Default: true for nav/footer. */
  end?: boolean;
}

function isPathActive(pathname: string, to: string, end?: boolean): boolean {
  if (to === '/') return pathname === '/';
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(to + '/');
}

export const Link: React.FC<LinkProps> = ({
  to,
  href,
  children,
  variant = 'default',
  className = '',
  external = false,
  onClick,
  end = false,
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  const active = Boolean(to && !href && isPathActive(pathname, to, end));
  const baseClass = 'kf-link';
  const activeClass = active ? `${baseClass}--active` : '';
  const classes = `${baseClass} ${baseClass}--${variant} ${activeClass} ${className}`.trim();

  // External link
  if (href) {
    return (
      <a
        href={href}
        className={classes}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  // Internal router link
  if (to) {
    return (
      <RouterLink to={to} className={classes} onClick={onClick}>
        {children}
      </RouterLink>
    );
  }

  // Default anchor
  return (
    <a href="#" className={classes} onClick={onClick}>
      {children}
    </a>
  );
};
